import { AuthContext } from '@forto-dev/frontend-infra-auth'
import { ReactQueryClientProvider } from '@forto-dev/frontend-infra-reactquery'
import type { NotificationWidgetRootProps } from '@forto-dev/insights'
import { Box, NotificationImportantOutlinedIcon, Tooltip } from '@freighthub/silhouette-core'
import { config } from 'app/config'
import { FC, lazy, Suspense, useState } from 'react'
import { useAuth } from 'service/useAuth'
import { useUIStore } from 'service/useUIStore'
import { LocalisationContext, useI18n } from 'utils/localisation'
import { logError } from 'utils/logger'
import { ErrorBoundary } from 'view/ErrorBoundary'
import { Loader } from 'view/Loader'

const NotificationWidgetLazy = lazy(async () => {
  try {
    const { NotificationWidgetRoot } = await import('@forto-dev/insights')
    return { default: NotificationWidgetRoot }
  } catch (e) {
    console.log('Error', e)
    return { default: () => <div>NotificationWidget is offline. Please contact the insights-integrations team</div> }
  }
})

const NotificationWidgetRoot: FC = () => {
  const [loadError, setLoadError] = useState(false)
  const store = useI18n()
  const authStore = useAuth()
  const { snack } = useUIStore()
  const { translate } = useI18n()

  const handleCatch = (error: Error) => {
    setLoadError(true)
    logError(error)
    snack.notifyError(translate('notification_widget-error_boundary-snack'))
  }

  const props: NotificationWidgetRootProps = {
    config: {
      WEBSOCKET_ENDPOINT: config.WEBSOCKET_ENDPOINT,
      NOTIFICATION_CENTER_ENDPOINT: config.NOTIFICATION_CENTER_ENDPOINT,
      APP_ENV: config.APP_ENV,
      ANALYTICS_ID: config.FULLSTORY_ANALYTICS_ID,
    },
  }

  return (
    <ErrorBoundary handleReactCatch={handleCatch}>
      {/* Old Notifications component will show up as a fallback if MicroFE fails to load  */}
      {loadError ? (
        <Tooltip title={translate('notification_widget-error_boundary-snack')}>
          <Box ml={1} display="flex">
            <NotificationImportantOutlinedIcon color="disabled" />
          </Box>
        </Tooltip>
      ) : (
        <LocalisationContext.Provider value={store}>
          <AuthContext.Provider value={authStore}>
            <ReactQueryClientProvider>
              <Suspense fallback={<Loader show />}>
                <NotificationWidgetLazy {...props} />
              </Suspense>
            </ReactQueryClientProvider>
          </AuthContext.Provider>
        </LocalisationContext.Provider>
      )}
    </ErrorBoundary>
  )
}

export default NotificationWidgetRoot
